import { useI18n } from '../../i18n';
import { arcPath, polar } from './geometry';

/**
 * The one picture that settles "why are my signs different": two zodiac rings
 * over the same sky. The outer ring is sidereal (Jyotish) with its start line
 * at the fixed stars; the inner ring is tropical (Western) with its start line
 * dragged ~24° back by precession. The shaded wedge between the two start
 * lines IS the ayanamsa.
 */

const CX = 170;
const CY = 120;
// Outer (sidereal) ring.
const S_OUT = 108;
const S_IN = 88;
// Inner (tropical) ring.
const T_OUT = 84;
const T_IN = 64;
/** How far the tropical start line has drifted back, in drawn degrees. */
const DRIFT = -24;

const TICKS = Array.from({ length: 12 }, (_, k) => k * 30);

export function AyanamsaDrift() {
  const { t } = useI18n();
  return (
    <svg
      viewBox="0 0 340 300"
      style={{ width: '100%', height: 'auto' }}
      role="img"
      aria-label={t('learn.fig.ayanamsa.alt')}
    >
      <defs>
        <marker
          id="ayanamsa-drift-arrow"
          viewBox="0 0 8 8"
          refX="6"
          refY="4"
          markerWidth="6"
          markerHeight="6"
          orient="auto"
        >
          <path d="M 0 0 L 8 4 L 0 8 Z" fill="var(--gold-ink)" />
        </marker>
      </defs>

      {/* The gap between the two start lines — the ayanamsa itself. */}
      <path d={ringWedge()} fill="var(--gold-soft)" stroke="none" />

      {/* Sidereal ring: locked to the stars. */}
      <circle cx={CX} cy={CY} r={S_OUT} fill="none" stroke="var(--border-strong)" strokeWidth="1" />
      <circle cx={CX} cy={CY} r={S_IN} fill="none" stroke="var(--border-strong)" strokeWidth="1" />
      {TICKS.map((deg) => {
        const [x1, y1] = polar(CX, CY, S_IN, deg);
        const [x2, y2] = polar(CX, CY, S_OUT, deg);
        return <line key={deg} x1={x1} y1={y1} x2={x2} y2={y2} stroke="var(--border-strong)" strokeWidth="1" />;
      })}

      {/* Tropical ring: the same twelve slices, slid back by the drift. */}
      <circle cx={CX} cy={CY} r={T_OUT} fill="none" stroke="var(--border)" strokeWidth="1" />
      <circle cx={CX} cy={CY} r={T_IN} fill="none" stroke="var(--border)" strokeWidth="1" />
      {TICKS.map((deg) => {
        const [x1, y1] = polar(CX, CY, T_IN, deg + DRIFT);
        const [x2, y2] = polar(CX, CY, T_OUT, deg + DRIFT);
        return <line key={deg} x1={x1} y1={y1} x2={x2} y2={y2} stroke="var(--border)" strokeWidth="1" />;
      })}

      {/* The two start lines, drawn heavier than the ticks. */}
      <line
        x1={polar(CX, CY, S_IN - 2, 0)[0]}
        y1={polar(CX, CY, S_IN - 2, 0)[1]}
        x2={polar(CX, CY, S_OUT + 5, 0)[0]}
        y2={polar(CX, CY, S_OUT + 5, 0)[1]}
        stroke="var(--mode)"
        strokeWidth="2"
      />
      <line
        x1={polar(CX, CY, T_IN - 2, DRIFT)[0]}
        y1={polar(CX, CY, T_IN - 2, DRIFT)[1]}
        x2={polar(CX, CY, T_OUT + 2, DRIFT)[0]}
        y2={polar(CX, CY, T_OUT + 2, DRIFT)[1]}
        stroke="var(--gold)"
        strokeWidth="2"
      />

      {/* First-sign labels, each in its own frame — the offset made visible. */}
      <text
        x={polar(CX, CY, (S_OUT + S_IN) / 2, 15)[0]}
        y={polar(CX, CY, (S_OUT + S_IN) / 2, 15)[1] + 3}
        textAnchor="middle"
        fontSize="8"
        fill="var(--text-dim)"
      >
        Mesha
      </text>
      <text
        x={polar(CX, CY, (T_OUT + T_IN) / 2, 15 + DRIFT)[0]}
        y={polar(CX, CY, (T_OUT + T_IN) / 2, 15 + DRIFT)[1] + 3}
        textAnchor="middle"
        fontSize="7.5"
        fill="var(--text-dim)"
      >
        Aries
      </text>

      {/* The drift, named at the centre with an arrow tracing the slide. */}
      <path
        d={arcPath(CX, CY, 52, -2, DRIFT + 2, 0)}
        fill="none"
        stroke="var(--gold-ink)"
        strokeWidth="1.5"
        markerEnd="url(#ayanamsa-drift-arrow)"
      />
      <text x={CX} y={CY - 2} textAnchor="middle" fontSize="11" fontWeight="600" fill="var(--gold-ink)">
        {t('learn.fig.ayanamsa.gap1')}
      </text>
      <text x={CX} y={CY + 14} textAnchor="middle" fontSize="10" fill="var(--gold-ink)">
        {t('learn.fig.ayanamsa.gap2')}
      </text>

      {/* Legend: which ring is which, colour-matched to the start lines. */}
      <line x1={78} y1={248} x2={98} y2={248} stroke="var(--mode)" strokeWidth="2" />
      <text x={106} y={251} fontSize="9" fill="var(--mode-ink)">
        {t('learn.fig.ayanamsa.sidereal')}
      </text>
      <line x1={78} y1={266} x2={98} y2={266} stroke="var(--gold)" strokeWidth="2" />
      <text x={106} y={269} fontSize="9" fill="var(--gold-ink)">
        {t('learn.fig.ayanamsa.tropical')}
      </text>

      <text x={170} y={292} textAnchor="middle" fontSize="10" fill="var(--text-dim)">
        {t('learn.fig.ayanamsa.note')}
      </text>
    </svg>
  );
}

/** Annulus wedge spanning both rings between the two start lines. */
function ringWedge(): string {
  const [x0, y0] = polar(CX, CY, S_OUT, DRIFT);
  const [x1, y1] = polar(CX, CY, S_OUT, 0);
  const [x2, y2] = polar(CX, CY, T_IN, 0);
  const [x3, y3] = polar(CX, CY, T_IN, DRIFT);
  return (
    `M ${x0} ${y0} A ${S_OUT} ${S_OUT} 0 0 1 ${x1} ${y1} ` +
    `L ${x2} ${y2} A ${T_IN} ${T_IN} 0 0 0 ${x3} ${y3} Z`
  );
}
